/**
 * Travel Resources Content
 * 
 * Contains all text content for travel-related documents and guides
 */

export interface TravelResource {
    title: string;
    content: string;
}

export const travelResources: Record<string, TravelResource> = {
    spOverseasTravel: {
        title: "SP Overseas Travel Page",
        content: `IOD PARC Overseas Travel Portal

Welcome to the IOD PARC Overseas travel page. This site will help you plan, prepare, and participate in safe and productive international assignments. It does not apply to domestic travel. Participation in overseas travel is voluntary, and employers have a duty of care for employees whenever they are at work, whether in the UK or abroad.

Not all travel will require every action listed here. The approach to risk management varies depending on the location, nature, and complexity of the travel. Travel to safe locations require fewer steps, while higher-risk travel requires more preparation, approval, and monitoring. Please contact the Operations Manager or Security Focal Point if you are arranging travel for Associates or in-country partners.

PLAN

When considering whether to bid:
• Assess potential travel, location, and assignment risks as part of the business case.
• MD (with Trading Board) must approve bids for FCDO Red zone work.
• MD approval required for Amber zone assignments.
• Include risk mitigation costs (insurance, secure accommodation, safe transport, equipment).

When an opportunity is secured (Individual):
• Check FCDO travel advisories (including Amber/Red zones).
• Check AIG country reports and consult clients for local risks.
• Check Fit for Travel for health/disease updates.
• Complete the Travel Risk Assessment. (Doc Hub)
• Complete Travel Authorisation Form. (Doc Hub)
• If travelling to Amber/Red zones or elevated-risk areas, complete a Security Plan with SFP support.
• Seek quotes for safe flights and accommodation, consulting Ops Manager as needed.

PREPARE

• Book flights and accommodation through approved providers.
• Complete UNDSS BSafe training (or equivalent).
• Register on the AIG app and sign up for alerts (Policy Number: 0010567780)
• Submit Travel Details Form at least 5 days before travel. (Doc hub)
• Participate in pre-travel briefing with SFP (if needed).
• Confirm check-in schedule with Duty Officer.
• Update risk assessment close to departure (if needed).
• Ensure Safety Plan actions (including security training) are completed.
• Agree team arrangements for crisis response with client and associates.
• Carry IOD PARC insurance policy, AIG booklet, and embassy contacts.

PARTICIPATE

• Confirm safe arrival with Duty Officer.
• Check in via WhatsApp or AIG app at agreed intervals.
• Follow actions in the Safety Plan.
• Attend client security briefings and follow directions.
• Use designated transport and comply with PPE/security requirements.
• Report all incidents immediately and complete Incident Report Form.
• Abide by the IOD PARC Code of Conduct and, where relevant, Supplier Code of Conduct.

Accessing Help Overseas

• Ensure your safety first.
• If unwell, seek local medical advice (ask client for recommendations).
• Contact Duty Officer immediately for support.
• Escalate to SFP or MD if necessary.
• Use AIG Assistance app for emergency medical/security support.

Key Contacts

• Duty Officer (assigned per trip)
• Security Focal Point: Pam Davis pam@iodparc.com
• Managing Director: Sadie Watson sadie@iodparc.com
• AIG (24/7) Emergency Assistance: +44 (0)1273 552 922

Roles & Responsibilities

Managing Director (MD): Leads Crisis Management Team, approves Amber/Red zone travel.
Security Focal Point (SFP): Maintains registers, supports risk assessments, liaises with insurers.
Duty Officer (Nominated Point of contact): 24/7 contact for travellers, manages incident logs.
Operations Manager: Oversees implementation of travel/security processes.
Project Leads: Ensure team compliance, briefings, and incident reporting.
Staff & Associates: Follow procedures, report incidents, check in as scheduled.`
    },
    travelFramework: {
        title: "Travel Framework Guide",
        content: `IOD PARC Travel Framework

This short guide explains how staff, associates, and managers should use the Travel Framework documents, and procedures. It complements the Security Risk Management Policy.

1. Travel Risk Assessment

• Complete for every trip, especially to Amber/Red FCDO rated areas.
• Identify risks across political, health, transport, accommodation, inclusion, and information security.
• Agree mitigation measures with the Security Focal Point.
• Required before travel authorisation.

2. Travel Authorisation Form

• Completed after the risk assessment.
• Sign-off path: Project Manager → Security Focal Point → MD (if high risk).
• Stored centrally by the Operations Manager.

3. Travel Details form

• Completed after the travel authorisation.
• Used to submit detailed travel plans: travel, accommodation, points of contact.
• Required before travel.
• Stored centrally by the Operations Manager.

4. Incident Report Form & Register

• Used to report all incidents and near misses within 24 hours.
• Submit form to Duty Officer or Security Focal Point.
• Security Focal Point maintains the register for organisational learning.

5. Role Descriptions

• Defines who does what during crises.
• Staff should familiarise themselves with their responsibilities.
• Duty Officer and SFP act as key contacts.

6. Crisis Log

• Used during live crises by the Duty Officer and CMT.
• Ensures all decisions, actions, and outcomes are recorded.
• Essential for after-action reviews.

7. Procedures

• Incident Reporting Procedure: Steps for notifying, documenting, and following up.
• Crisis Management Plan: When and how the Crisis Management Team is activated, phases of response, communication protocols, and recovery.`
    },
    travelRiskProcedure: {
        title: "Travel Risk Procedure",
        content: `Incident Reporting Procedure

Purpose

To ensure all incidents, accidents, near misses, and security events are reported promptly, escalated appropriately, and logged for organisational learning.

Scope

Applies to all IOD PARC employees, associates, and partners while travelling or working on behalf of IOD PARC.

Procedure

Immediate Response

• Traveller ensures safety of self and others.
• Seek urgent medical/security assistance as required.
• Contact Duty Officer (24/7) as soon as practicable.

Notification

• Duty Officer notifies Security Focal Point immediately.
• Managing Director is notified if incident is serious (medical emergency, arrest, abduction, reputational issue).
• Where applicable, notify Client focal point.
• Insurance provider (AIG) contacted by SFP or MD.

Documentation

• Traveller completes Incident Report Form within 24 hours.
• Duty Officer logs incident in Crisis Log Form if escalated.
• SFP adds record to the Incident Register.

Follow-Up

• SFP coordinates aftercare, debrief, and lessons learned.
• Project Lead ensures implications are fed into project safety planning.
• Significant incidents trigger an After-Action Review within 10 working days.`
    },
    crisisManagement: {
        title: "Crisis Management Plan",
        content: `IOD PARC Crisis Management Plan

Purpose

To provide clear guidance on how IOD PARC manages and responds to crises affecting its personnel, operations, or reputation.

Scope

Applies to all IOD PARC staff, associates, and partners on overseas travel.

1. Activation Triggers

• Serious incident (injury, death, arrest, abduction, major security event).
• Loss of contact with traveller for >12 hours in medium/high-risk contexts.
• Escalation of local risk (e.g., protests, outbreak, natural disaster, FCDO 'do not travel').
• Reputational risk (e.g., media/online exposure linked to staff/assignment).

2. Crisis Management Team (CMT)

Composition:
• Managing Director (CMT Lead)
• Security Focal Point (Coordinator/log incidents)
• Duty Officer (Communicate with traveller)
• Project Manager/Team Lead (context-specific input)
• HR Representative (staff wellbeing, family liaison)
• Finance/Operations (resources, logistics, insurance claims)
• Communications/IT (internal and external messaging, digital security)

Authority: The MD holds final decision-making authority. The CMT has authority to suspend travel, evacuate personnel, or halt operations.

3. Response Phases

a) Initial Response (0–2 hours)
• Duty Officer confirms incident and logs details.
• CMT Lead (MD) convenes the CMT.
• Communications protocols initiated (secure channels, WhatsApp, Teams, encrypted email).

b) Stabilisation (2–12 hours)
• Assess scale of crisis and risks.
• Ensure staff safety and medical/security assistance.
• Liaise with insurers (AIG), embassies, and clients as appropriate.
• Assign spokesperson for internal/external communications.

c) Ongoing Management (12 hours–resolution)
• Maintain regular contact with affected staff.
• Update Board, insurers, and clients as required.
• Document all actions in Crisis Log.

d) Resolution & Recovery
• Confirm staff safety and operational continuity.
• Conduct After-Action Review within 10 working days.
• Update procedures and training as needed.

4. Communication Protocols

• Primary channel: WhatsApp group (or AIG app for check-ins).
• Secondary channels: Email, phone, satellite phone (if available).
• Sensitive information shared only via secure/approved channels.

5. Family and Staff Support

• HR leads on liaison with family members where needed.
• Access to counselling or psychosocial support offered.
• Staff debrief conducted post-incident.

6. Records and Lessons Learned

• All crisis actions logged in Crisis Log.
• Incident Register updated.
• Lessons learned circulated to staff and integrated into security briefings.`
    },
    rolesResponsibilities: {
        title: "Roles and Responsibilities",
        content: `Role Descriptions (Crisis Management Plan)

Managing Director (Crisis Lead)

• Overall decision-making authority
• Approves high-risk travel and crisis response actions
• Escalates to Trading Board if required

Security Focal Point (Coordinator)

• Maintains risk register and incident reports
• Advises on risk assessments and safety plans
• Coordinates with AIG, insurers, external providers
• Supports Duty Officer

Duty Officer (Nominated Point of Contact)

• First point of contact for travelling staff
• Maintains communication log during incidents
• Escalates to SFP and MD as required

Project Manager / Team Lead

• Ensures risk assessments and safety plans are in place
• Confirms pre-travel briefings completed
• Liaises with clients on duty of care issues

Staff, Associates

• Comply with security procedures and training
• Report incidents immediately
• Follow check-in protocols and emergency instructions`
    }
};

// Order of tabs
export const travelResourcesOrder = [
    'spOverseasTravel',
    'travelFramework',
    'travelRiskProcedure',
    'crisisManagement',
    'rolesResponsibilities'
];
